import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useCommandPalette } from '../../contexts/CommandPaletteContext';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { getUserDirectory, USER_DIRECTORY_QUERY_KEY } from '../../services/userDirectory';
import { useRBAC } from '../../contexts/RBACContext';
import {
  Search,
  LayoutDashboard,
  Users,
  FolderKanban,
  Briefcase,
  CalendarDays,
  Settings,
  BarChart3,
  Clock,
  User,
  ArrowRight,
  Command,
  Shield,
  Key,
  Loader2,
  TrendingUp
} from 'lucide-react';
import { cn } from '../../utils/cn';
import toast from 'react-hot-toast';

interface CommandItem {
  id: string;
  type: 'navigation' | 'project' | 'user' | 'pod' | 'action' | 'totp';
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string[];
  asyncAction?: boolean;
}

interface CommandGroup {
  id: string;
  title: string;
  items: CommandItem[];
}

export const CommandPalette: React.FC = () => {
  const { isOpen, close } = useCommandPalette();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // `/projects` is gated `projects:view` GLOBAL — don't fire it for roles that
  // would only get a 403 (decided only once RBAC has loaded; hasPermission is
  // optimistic while loading).
  const { hasPermission, isLoading: rbacLoading } = useRBAC();
  const canListProjects = !rbacLoading && hasPermission('projects', 'view', 'global');

  // Fetch data for search
  const { data: projectsData } = useQuery({
    queryKey: ['commandPaletteProjects'],
    queryFn: () => api.getProjects({ limit: 50 }),
    enabled: isOpen && canListProjects,
    staleTime: 30000,
  });

  // People come from the lightweight directory (open to everyone), searched
  // server-side so matches beyond the first page are found.
  const peopleQuery = query.trim();
  const { data: directoryUsers } = useQuery({
    queryKey: [USER_DIRECTORY_QUERY_KEY, 'commandPalette', peopleQuery],
    queryFn: () => getUserDirectory({ q: peopleQuery || undefined, limit: 50 }),
    enabled: isOpen,
    staleTime: 30000,
    placeholderData: previous => previous,
  });

  const { data: podsData } = useQuery({
    queryKey: ['commandPalettePodNames'],
    queryFn: () => api.getPodNames(),
    enabled: isOpen,
    staleTime: 30000,
  });

  const { data: totpData } = useQuery({
    queryKey: ['commandPaletteTotpCodes'],
    queryFn: () => api.get('/totp-codes', { isActive: 'true', limit: 100 }),
    enabled: isOpen,
    staleTime: 30000,
  });

  // Generate TOTP token and copy to clipboard
  const generateAndCopyToken = useCallback(async (totpId: string, totpName: string) => {
    setGeneratingId(totpId);
    try {
      const response = await api.post(`/totp-codes/${totpId}/generate`);
      const token = (response as any).result?.token;
      const remainingSeconds = (response as any).result?.remainingSeconds;
      if (token) {
        await navigator.clipboard.writeText(token);
        toast.success(
          () => (
            <div className="flex flex-col gap-1">
              <div className="font-medium">{totpName}</div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-lg font-bold tracking-wider">{token}</span>
                <span className="text-xs text-gray-400">copied!</span>
              </div>
              {remainingSeconds && (
                <div className="text-xs text-gray-400">Valid for {remainingSeconds}s</div>
              )}
            </div>
          ),
          { duration: 5000, id: 'totp-copied' }
        );
        close();
      } else {
        toast.error('Failed to generate code');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to generate code');
    } finally {
      setGeneratingId(null);
    }
  }, [close]);

  // Static navigation commands
  const navigationCommands: CommandItem[] = useMemo(() => [
    { id: 'nav-dashboard', type: 'navigation', title: 'Dashboard', subtitle: 'Go to dashboard', icon: <LayoutDashboard className="w-4 h-4" />, action: () => navigate('/'), keywords: ['home', 'main'] },
    { id: 'nav-projects', type: 'navigation', title: 'Build Projects', subtitle: 'View all build projects', icon: <FolderKanban className="w-4 h-4" />, action: () => navigate('/builds'), keywords: ['builds', 'projects'] },
    { id: 'nav-retainers', type: 'navigation', title: 'Retainers', subtitle: 'View all retainers', icon: <Briefcase className="w-4 h-4" />, action: () => navigate('/retainers'), keywords: ['retainer', 'client'] },
    { id: 'nav-pods', type: 'navigation', title: 'Pods', subtitle: 'Manage pods', icon: <Users className="w-4 h-4" />, action: () => navigate('/pods'), keywords: ['team', 'group'] },
    { id: 'nav-time', type: 'navigation', title: 'Time Tracking', subtitle: 'View time entries', icon: <Clock className="w-4 h-4" />, action: () => navigate('/time'), keywords: ['hours', 'tracking', 'timesheet'] },
    { id: 'nav-absences', type: 'navigation', title: 'Team Calendar', subtitle: 'Team absence calendar', icon: <CalendarDays className="w-4 h-4" />, action: () => navigate('/team-calendar'), keywords: ['holiday', 'vacation', 'leave', 'pto', 'absences'] },
    { id: 'nav-absence-overview', type: 'navigation', title: 'Absence Overview', subtitle: 'Absence summary and capacity impact', icon: <TrendingUp className="w-4 h-4" />, action: () => navigate('/absence-overview'), keywords: ['absence', 'overview', 'capacity', 'summary'] },
    { id: 'nav-users', type: 'navigation', title: 'Users', subtitle: 'Manage users', icon: <User className="w-4 h-4" />, action: () => navigate('/users'), keywords: ['people', 'employees', 'team'] },
    { id: 'nav-analytics', type: 'navigation', title: 'Analytics', subtitle: 'View analytics', icon: <BarChart3 className="w-4 h-4" />, action: () => navigate('/analytics'), keywords: ['reports', 'charts', 'stats'] },
    { id: 'nav-settings', type: 'navigation', title: 'Settings', subtitle: 'Personal settings', icon: <Settings className="w-4 h-4" />, action: () => navigate('/settings'), keywords: ['preferences', 'config'] },
    { id: 'nav-admin', type: 'navigation', title: 'System Admin', subtitle: 'Administration panel', icon: <Shield className="w-4 h-4" />, action: () => navigate('/admin/system'), keywords: ['administration', 'system'] },
  ], [navigate]);

  // Dynamic items from API data
  const projectCommands: CommandItem[] = useMemo(() => {
    const projects = (projectsData as any)?.results || (projectsData as any)?.data?.results || [];
    return projects.slice(0, 10).map((project: any) => ({
      id: `project-${project._id}`,
      type: 'project' as const,
      title: project.name,
      subtitle: project.client?.name || project.type,
      icon: <FolderKanban className="w-4 h-4" />,
      action: () => navigate(`/projects/${project._id}`),
      keywords: [project.jiraKey, project.client?.name].filter(Boolean),
    }));
  }, [projectsData, navigate]);

  const userCommands: CommandItem[] = useMemo(() => {
    const users = directoryUsers ?? [];
    return users.slice(0, 10).map(user => ({
      id: `user-${user._id}`,
      type: 'user' as const,
      title: user.name || user.email,
      subtitle: user.department || 'Team Member',
      icon: <User className="w-4 h-4" />,
      action: () => navigate(`/user/${user._id}`),
      keywords: [user.email].filter(Boolean),
    }));
  }, [directoryUsers, navigate]);

  const podCommands: CommandItem[] = useMemo(() => {
    // `/pods/names` only carries `{ id, name }` — the old `_id`/`type` reads were
    // always undefined, which collapsed every entry onto the key `pod-undefined`.
    const pods = podsData?.results ?? [];
    return pods.slice(0, 10).map(pod => ({
      id: `pod-${pod.id}`,
      type: 'pod' as const,
      title: pod.name,
      subtitle: 'Pod',
      icon: <Users className="w-4 h-4" />,
      action: () => navigate(`/pods`),
      keywords: ['pod', 'team'],
    }));
  }, [podsData, navigate]);

  // TOTP commands for quick code generation
  const totpCommands: CommandItem[] = useMemo(() => {
    const codes = (totpData as any)?.results || [];
    return codes.map((code: any) => ({
      id: `totp-${code._id}`,
      type: 'totp' as const,
      title: code.name,
      subtitle: code.issuer ? `${code.issuer} • Generate & copy code` : 'Generate & copy code',
      icon: generatingId === code._id
        ? <Loader2 className="w-4 h-4 animate-spin" />
        : <Key className="w-4 h-4" />,
      action: () => generateAndCopyToken(code._id, code.name),
      keywords: ['2fa', 'totp', 'code', 'auth', 'authenticator', code.issuer, ...(code.tags || []), code.projectId?.name, code.projectId?.jiraKey].filter(Boolean),
      asyncAction: true,
    }));
  }, [totpData, generateAndCopyToken, generatingId]);

  // Filter and group commands
  const filteredGroups: CommandGroup[] = useMemo(() => {
    const lowerQuery = query.toLowerCase().trim();
    const queryWords = lowerQuery.split(/\s+/).filter(Boolean);

    const filterItems = (items: CommandItem[]) => {
      if (!lowerQuery) return items;
      return items.filter(item => {
        const searchText = [item.title, item.subtitle, ...(item.keywords || [])].join(' ').toLowerCase();
        // All query words must be present in the search text
        return queryWords.every(word => searchText.includes(word));
      });
    };

    const groups: CommandGroup[] = [];

    // Show TOTP codes first if any query word is '2fa', 'totp', or 'auth'
    const is2faQuery = queryWords.some(word => ['2fa', 'totp', 'auth'].includes(word));

    if (is2faQuery) {
      const totpItems = filterItems(totpCommands);
      if (totpItems.length > 0) {
        groups.push({ id: 'totp', title: '2FA Codes', items: totpItems });
      }
    }

    const navItems = filterItems(navigationCommands);
    if (navItems.length > 0) {
      groups.push({ id: 'navigation', title: 'Navigation', items: navItems });
    }

    const projItems = filterItems(projectCommands);
    if (projItems.length > 0) {
      groups.push({ id: 'projects', title: 'Projects', items: projItems });
    }

    const userItems = filterItems(userCommands);
    if (userItems.length > 0) {
      groups.push({ id: 'users', title: 'Users', items: userItems });
    }

    const podItems = filterItems(podCommands);
    if (podItems.length > 0) {
      groups.push({ id: 'pods', title: 'Pods', items: podItems });
    }

    // Show TOTP at the end if not a 2fa query but still matches
    if (!is2faQuery) {
      const totpItems = filterItems(totpCommands);
      if (totpItems.length > 0) {
        groups.push({ id: 'totp', title: '2FA Codes', items: totpItems });
      }
    }

    return groups;
  }, [query, navigationCommands, projectCommands, userCommands, podCommands, totpCommands]);

  // Flatten items for keyboard navigation
  const allItems = useMemo(() =>
    filteredGroups.flatMap(group => group.items),
    [filteredGroups]
  );

  // Reset state when opening/closing
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Execute selected command
  const executeCommand = useCallback((item: CommandItem) => {
    close();
    item.action();
  }, [close]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, allItems.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (allItems[selectedIndex]) {
            executeCommand(allItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          close();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, allItems, selectedIndex, executeCommand, close]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && allItems.length > 0) {
      const selectedElement = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      selectedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, allItems.length]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] overflow-y-auto" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={close} />

      {/* Modal container */}
      <div className="flex min-h-full items-start justify-center p-4 pt-[15vh]">
        <div className="relative w-full max-w-xl transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5">
          {/* Search input */}
          <div className="flex items-center border-b border-gray-200 px-4">
            <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              className="w-full border-0 bg-transparent py-4 pl-3 pr-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 text-base"
              placeholder="Search for pages, projects, users..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <kbd className="hidden sm:flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs text-gray-500 font-medium">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-2">
            {allItems.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No results found for "{query}"</p>
              </div>
            ) : (
              filteredGroups.map((group) => (
                <div key={group.id} className="mb-2">
                  <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {group.title}
                  </div>
                  {group.items.map((item) => {
                    const globalIndex = allItems.findIndex(i => i.id === item.id);
                    const isSelected = globalIndex === selectedIndex;
                    return (
                      <button
                        key={item.id}
                        data-index={globalIndex}
                        onClick={() => executeCommand(item)}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                        className={cn(
                          'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                          isSelected ? 'bg-blue-50 text-blue-900' : 'text-gray-700 hover:bg-gray-50'
                        )}
                      >
                        <span className={cn(
                          'flex-shrink-0 p-1.5 rounded-md',
                          isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                        )}>
                          {item.icon}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{item.title}</div>
                          {item.subtitle && (
                            <div className="text-sm text-gray-500 truncate">{item.subtitle}</div>
                          )}
                        </div>
                        {isSelected && (
                          <ArrowRight className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer with hints */}
          <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-2">
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">↑</kbd>
                <kbd className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">↵</kbd>
                select
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Command className="w-3 h-3" />
              <span>K to open</span>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
